import { isCourseSelfPaced } from '@/composables/useSelfPacedCourse.js';
import FeatureMiddleware from '@/middleware/FeatureMiddleware.js';
import GateKeeperMiddleware from '@/middleware/GateKeeperMiddleware.js';
import Course from '@/models/Course.js';
import store from '@/store/Index.js';

async function redirectIfAsynchronousCourse(to, from, next) {
    const courseId = Number(to.params.courseId);

    if (!courseId || !Number.isFinite(courseId)) {
        return next();
    }

    let course = Course.query().find(courseId);

    if (!course) {
        await store.dispatch('Courses/fetchMinimal', { id: courseId });
        course = Course.query().find(courseId);
    }

    if (course && isCourseSelfPaced(course)) {
        return next({
            name: 'course-dashboard',
            params: { courseId: to.params.courseId },
        });
    }

    return next();
}

/**
 * Instructor Story 15, AC-25 and 868mm46wt: the legacy weekly attendance grid is not an editing surface on Edverum Attend courses.
 * Every visit to the old page passes through here and is replaced by Attendance History, whatever the user's ATTEND flag or live
 * check result. Courses on another attendance solution keep the existing page.
 */
async function redirectToHistoryOnAttendCourse(to, from, next) {
    const courseId = Number(to.params.courseId);

    if (!courseId || !Number.isFinite(courseId)) {
        return next();
    }

    let course = Course.query().with('course_settings').find(courseId);

    if (!course?.course_settings) {
        await store.dispatch('Courses/fetchOne', { id: courseId });
        course = Course.query().with('course_settings').find(courseId);
    }

    if (course?.course_settings?.attendance_solution !== 'edverum_attend') {
        return next();
    }

    return next({
        name: 'course-attendance-history',
        params: { courseId: to.params.courseId },
        replace: true,
    });
}

/**
 * Allow the grid, and the attendance history and past-session roster that replace it on Edverum Attend courses,
 * when the user can view all attendances globally, via a group, or via their enrollment in this course.
 *
 * @param {{ gateKeeper: object, to: object }} context
 * @returns {boolean}
 */
export function canViewCourseAttendance({ gateKeeper, to }) {
    const permission = 'view all attendances';

    if (gateKeeper.userHasPermission(permission) || gateKeeper.user.hasGroupPermissionTo(permission)) {
        return true;
    }

    const courseId = Number(to.params?.courseId);

    if (!Number.isFinite(courseId)) {
        return false;
    }

    return Boolean(gateKeeper.user.isEnrolledWithPermission({ id: courseId }, permission));
}

export default [
    {
        name: 'course-attendance',
        path: '/courses/:courseId/attendance',
        component: () => import('@/pages/courses/attendance/AttendanceIndex.vue'),
        beforeEnter: [redirectIfAsynchronousCourse, redirectToHistoryOnAttendCourse],
        meta: {
            title: 'Attendance',
            middleware: [GateKeeperMiddleware],
            ability: canViewCourseAttendance,
            // Full-bleed this page so it uses the full page width.
            dataTablePage: true,
        },
    },
    // Instructor Story 15, AC-23, AC-25: the course's attendance history, and a past session's roster opened from it read-only.
    {
        name: 'course-attendance-history',
        path: '/courses/:courseId/attendance/history',
        component: () => import('@/pages/courses/attendance/AttendanceHistory.vue'),
        beforeEnter: [redirectIfAsynchronousCourse],
        meta: {
            title: 'Attendance history',
            middleware: [GateKeeperMiddleware],
            ability: canViewCourseAttendance,
            dataTablePage: true,
        },
    },
    {
        name: 'course-attendance-session',
        path: '/courses/:courseId/attendance/sessions/:sessionId(\\d+)',
        component: () => import('@/pages/courses/attendance/AttendanceSessionRoster.vue'),
        beforeEnter: [redirectIfAsynchronousCourse],
        meta: {
            title: 'Attendance history',
            middleware: [GateKeeperMiddleware],
            ability: canViewCourseAttendance,
            dataTablePage: true,
        },
    },
    {
        name: 'course-attendance-hours',
        path: '/courses/:courseId/attendance/hours',
        component: () => import('@/pages/courses/attendance/AttendanceHoursReport.vue'),
        beforeEnter: [FeatureMiddleware('ATTEND'), redirectIfAsynchronousCourse],
        meta: {
            title: 'Attendance hours',
            // Admin Story 10, AC-11: the dedicated reporting permission is checked by the page itself, which
            // shows the refusal in place; the API refuses a direct request too.
            dataTablePage: true,
        },
    },
];
